import { useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import { ConfidenceLevel, IdentityJustificationTypeItem } from '@/core/models/identity-justification.model'
import { IdentityJustificationFormDialog } from './IdentityJustificationFormDialog'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import {
  useGetAdminEstablishmentQuery,
  useUpdateAdminEstablishmentMutation,
} from '@/core/services/admin'
import {
  useDeleteIdentityJustificationTypeMutation,
  useUpdateIdentityJustificationTypesMutation,
} from '@/core/services/establishment'
import { useSnackbar } from 'notistack'

const getConfidenceLevelLabel = (level: ConfidenceLevel): string => {
  switch (level) {
    case ConfidenceLevel.HIGH:
      return t`High`
    case ConfidenceLevel.LOW:
      return t`Low`
  }
}

const getConfidenceLevelColor = (level: ConfidenceLevel): 'success' | 'default' => {
  switch (level) {
    case ConfidenceLevel.HIGH:
      return 'success'
    case ConfidenceLevel.LOW:
      return 'default'
  }
}

export function IdentityJustificationConfigTable() {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()
  const { data: establishment, isLoading, error } = useGetAdminEstablishmentQuery()
  const establishmentId = establishment?._id
  const types = establishment?.identityJustificationTypes || []
  const autoValidateIdentity = establishment?.autoValidateIdentity

  const [updateItem] = useUpdateIdentityJustificationTypesMutation()
  const [deleteItem] = useDeleteIdentityJustificationTypeMutation()
  const [updateEstablishment] = useUpdateAdminEstablishmentMutation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<IdentityJustificationTypeItem | null>(null)

  const handleAdd = () => {
    setEditingItem(null)
    setDialogOpen(true)
  }

  const handleEdit = (item: IdentityJustificationTypeItem) => {
    setEditingItem(item)
    setDialogOpen(true)
  }

  const handleDelete = async (item: IdentityJustificationTypeItem) => {
    if (establishmentId) {
      try {
        await deleteItem({ establishmentId, _id: item._id }).unwrap()
        enqueueSnackbar(t`Identity justification type deleted successfully`, { variant: 'success' })
      } catch {
        enqueueSnackbar(t`Failed to delete identity justification type`, { variant: 'error' })
      }
    }
  }

  const handleSave = async (item: IdentityJustificationTypeItem) => {
    if (establishmentId) {
      const payload = { _id: item._id, label: item.label, confidenceLevel: item.confidenceLevel }
      try {
        await updateItem({ establishmentId, item: payload }).unwrap()
        const isEdit = !!editingItem?._id
        enqueueSnackbar(isEdit ? t`Identity justification type updated successfully` : t`Identity justification type created successfully`, {
          variant: 'success',
        })
        setEditingItem(null)
        setDialogOpen(false)
      } catch {
        enqueueSnackbar(t`Failed to save identity justification type`, { variant: 'error' })
      }
    }
  }

  const handleCloseDialog = () => {
    setEditingItem(null)
    setDialogOpen(false)
  }
  const handleToggleAutoValidateIdentity = async () => {
    if (establishmentId) {
      try {
        await updateEstablishment({ establishmentId, autoValidateIdentity: !autoValidateIdentity }).unwrap()
        enqueueSnackbar(t`Identity validation setting updated`, { variant: 'success' })
      } catch {
        enqueueSnackbar(t`Failed to update identity validation setting`, { variant: 'error' })
      }
    }
  }

  if (error) {
    return (
      <Alert severity="error">
        <Trans>Failed to load identity justification types</Trans>
      </Alert>
    )
  }

  return (
    <Stack spacing={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">
          <Trans>Identity justification settings</Trans>
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={handleAdd}>
          <Trans>Add justification type</Trans>
        </Button>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <FormControlLabel
            control={<Switch checked={autoValidateIdentity} onChange={handleToggleAutoValidateIdentity} color="info" />}
            label={t`Automatically validate identities with high confidence level`}
          />
          <Chip
            size="small"
            label={autoValidateIdentity ? t`Enabled` : t`Disabled`}
            color={autoValidateIdentity ? 'info' : 'default'}
            variant={autoValidateIdentity ? 'filled' : 'outlined'}
          />
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Trans>Label</Trans>
              </TableCell>
              <TableCell>
                <Trans>Code</Trans>
              </TableCell>
              <TableCell>
                <Trans>Confidence level</Trans>
              </TableCell>
              <TableCell align="right">
                <Trans>Actions</Trans>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Trans>Loading...</Trans>
                </TableCell>
              </TableRow>
            ) : types.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Trans>No justification types configured</Trans>
                </TableCell>
              </TableRow>
            ) : (
              types.map((item, index) => (
                <TableRow key={`${item._id ?? index}`}>
                  <TableCell>{item.label}</TableCell>
                  <TableCell>
                    <code>{item._id}</code>
                  </TableCell>
                  <TableCell>
                    <Chip label={getConfidenceLevelLabel(item.confidenceLevel)} color={getConfidenceLevelColor(item.confidenceLevel)} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <IdentityJustificationFormDialog
        open={dialogOpen}
        initialValues={editingItem ?? { _id: '', label: '', confidenceLevel: ConfidenceLevel.LOW }}
        onClose={handleCloseDialog}
        onSave={handleSave}
      />
    </Stack>
  )
}
